import { BaseTransport, createPromiseBuffer, getTransportBody, noop, PromiseBuffer, VERSION } from "@grafana/faro-core";
import { RumTransportItem, RumFetchTransportOptions } from "./types";
import { getRumTransportBody } from "./utils";
import { getCurrentTimestamp } from "@grafana/faro-web-sdk";
const DEFAULT_BUFFER_SIZE = 30;
const DEFAULT_CONCURRENCY = 5; // chrome supports 10 total, firefox 17
const DEFAULT_RATE_LIMIT_BACKOFF_MS = 5000;
const REPORT_BUFFER_FULL_TIMEOUT = 10000;

export class RumFetchTransport extends BaseTransport {
  readonly name = "@grafana/faro-web-sdk:transport-rum-fetch";
  readonly version = VERSION;

  promiseBuffer: PromiseBuffer<Response | void>;

  private readonly rateLimitBackoffMs: number;
  private readonly getNow: () => number;
  private disabledUntil: Date = new Date();
  private options: RumFetchTransportOptions;
  private bufferFullCount = 0;
  private beaconFailed = false;

  private interval: NodeJS.Timeout | null = null;

  constructor(options: RumFetchTransportOptions) {
    super();
    this.options = options;
    this.rateLimitBackoffMs = options.defaultRateLimitBackoffMs ?? DEFAULT_RATE_LIMIT_BACKOFF_MS;
    this.getNow = options.getNow ?? (() => Date.now());

    this.promiseBuffer = createPromiseBuffer({
      size: options.bufferSize ?? DEFAULT_BUFFER_SIZE,
      concurrency: options.concurrency ?? DEFAULT_CONCURRENCY
    });
  }

  async send(item: RumTransportItem[]): Promise<void> {
    try {
      if (this.disabledUntil > new Date(this.getNow())) {
        this.logWarn(`Dropping transport item due to too many requests. Backoff until ${this.disabledUntil}`);
        return Promise.resolve();
      }
      const { url, requestOptions, apiKey, useSendBeacon } = this.options;
      const { headers, ...restOfRequestOptions } = requestOptions ?? {};
      if (useSendBeacon) {
        if ("sendBeacon" in navigator) {
          this.sendPayloadUsingBeacon(item, apiKey ?? "", url);
        } else {
          await this.sendPayloadUsingFetch(item, apiKey ?? "", url, headers, restOfRequestOptions);
        }
      } else {
        this.sendPayloadUsingFetch(item, apiKey ?? "", url, headers, restOfRequestOptions);
      }
    } catch (err) {
      if ("sendBeacon" in navigator) {
        this.bufferFullCount++;
        const bufferFullLimit = this.options.notifyOnBufferFullLimit ? this.options.notifyOnBufferFullLimit : 5;
        if (this.bufferFullCount >= bufferFullLimit) {
          this.sendBufferFullCount(item);
          this.bufferFullCount = 0;
          if (this.interval !== null) {
            clearInterval(this.interval);
          }
        } else {
          const interval = setInterval(() => {
            this.sendBufferFullCount(item);
            clearInterval(interval);
          }, REPORT_BUFFER_FULL_TIMEOUT);
          this.interval = interval;
        }
      }
    }
  }

  private sendPayloadUsingBeacon = (item: RumTransportItem[], apiKey: string, url: string): void => {
    const body = JSON.stringify(getRumTransportBody(item, apiKey));
    const success = navigator.sendBeacon(url, body);
    if (!success) {
      if (!this.beaconFailed) {
        this.logWarn("Failed to send data using navigator.sendBeacon.");
        if (navigator.sendBeacon.toString().includes("NS_ERROR_CONTENT_BLOCKED")) {
          this.logWarn("NS_ERROR_CONTENT_BLOCKED may be the cause.");
        }
        this.beaconFailed = true;
      }
    }
  };

  private sendPayloadUsingFetch = (
    item: RumTransportItem[],
    apiKey: string,
    url: string,
    headers: Record<string, string> | undefined,
    restOfRequestOptions: RequestInit | undefined
  ): PromiseLike<void | Response> => {
    return this.promiseBuffer.add(() => {
      const body = JSON.stringify(getTransportBody(item));

      return fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(headers ?? {}),
          ...(apiKey ? { "x-api-token": apiKey } : {})
        },
        body,
        keepalive: true,
        ...(restOfRequestOptions ?? {})
      })
        .then((response) => {
          if (response.status === 429) {
            this.disabledUntil = this.getRetryAfterDate(response);
            this.logWarn(`Too many requests, backing off until ${this.disabledUntil}`);
          }
          // read the body so the connection can be closed
          response.text().catch(noop);
          return response;
        })
        .catch((err) => {
          if (navigator.onLine) {
            if ("sendBeacon" in navigator) {
              this.sendPayloadUsingBeacon(item, apiKey, url);
            }
          }
        });
    });
  };

  private sendBufferFullCount = (item: RumTransportItem[]): void => {
    const body = getTransportBody(item);
    const measurement = [
      {
        context: {},
        timestamp: `${getCurrentTimestamp()}`,
        type: "buffer_full",
        values: {
          bufferFullCount: this.bufferFullCount,
          transportbufferSize: this.options.bufferSize ?? DEFAULT_BUFFER_SIZE
        }
      }
    ];
    const payload = {
      measurements: measurement,
      meta: body?.meta,
      xApiToken: this.options.apiKey ? this.options.apiKey : ""
    };
    navigator.sendBeacon(this.options.url, JSON.stringify(payload));
  };

  override getIgnoreUrls(): Array<string | RegExp> {
    return [this.options.url];
  }

  override isBatched(): boolean {
    return true;
  }

  private getRetryAfterDate(response: Response): Date {
    const now = this.getNow();
    const retryAfterHeader = response.headers.get("Retry-After");

    if (retryAfterHeader) {
      const delay = Number(retryAfterHeader);

      if (!isNaN(delay)) {
        return new Date(delay * 1000 + now);
      }

      const date = Date.parse(retryAfterHeader);

      if (!isNaN(date)) {
        return new Date(date);
      }
    }

    return new Date(now + this.rateLimitBackoffMs);
  }
}
